import React from "react";
import { Link } from "react-router-dom";
import * as Phosphor from "@phosphor-icons/react";
import { ArrowRight, CaretRight } from "@phosphor-icons/react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { Overline, Container, btn } from "@/components/ui-kit";
import { IMAGES } from "@/lib/content";

const Icon = ({ name, ...props }) => {
  const C = Phosphor[name] || Phosphor.Circle;
  return <C {...props} />;
};

const DEV_IMG = {
  residential: "https://images.unsplash.com/photo-1571979622878-622d38ec238c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwzfHxhZXJpYWwlMjBzdWJ1cmJhbiUyMHJlc2lkZW50aWFsJTIwbmVpZ2hib3Job29kJTIwZGV2ZWxvcG1lbnR8ZW58MHx8fHwxNzg2NjU3NTk5fDA&ixlib=rb-4.1.0&q=85",
  construction: "https://images.unsplash.com/photo-1773215023063-e662ea91a69c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxuZXclMjByZXNpZGVudGlhbCUyMGNvbnN0cnVjdGlvbiUyMHNpdGUlMjBhZXJpYWwlMjBmcmFtaW5nfGVufDB8fHx8MTc4NjY1NzU5OXww&ixlib=rb-4.1.0&q=85",
};

const STAGES = [
  {
    n: "01", icon: "MagnifyingGlass", title: "Identify",
    lead: "Find strategically located opportunities.",
    body: "We source land in the path of growth through relationships with owners, brokers, and local professionals — including off-market parcels that never reach the broader market.",
  },
  {
    n: "02", icon: "ClipboardText", title: "Evaluate",
    lead: "Review zoning, utilities, access, market conditions, environmental constraints, and development feasibility.",
    body: "Each opportunity is underwritten against a disciplined checklist before we commit.",
    chipsLabel: "What we analyze",
    chips: ["Zoning", "Utilities", "Access", "Environmental considerations", "Site constraints", "Market demand", "Development feasibility", "Financial feasibility"],
  },
  {
    n: "03", icon: "PuzzlePiece", title: "Structure",
    lead: "Determine acquisition, investment, and development strategy.",
    body: "We define the right acquisition and capital structure — outright purchase, option, or joint venture — aligned to the opportunity, timeline, and risk profile.",
  },
  {
    n: "04", icon: "Gavel", title: "Entitle",
    lead: "Work with engineers, planners, municipalities, and consultants through zoning and entitlement processes.",
    body: "Entitlement is a team effort. We coordinate the professionals required to advance a plan through approval.",
    chipsLabel: "Who we work with",
    chips: ["Municipalities", "Civil engineers", "Land planners", "Architects", "Attorneys", "Environmental consultants", "Utility providers"],
  },
  {
    n: "05", icon: "Buildings", title: "Develop",
    lead: "Prepare the property for residential development.",
    body: "We advance site work, infrastructure, and horizontal development to make well-located land ready for homebuilding.",
  },
  {
    n: "06", icon: "TrendUp", title: "Create Value",
    lead: "Execute the strategy through development, builder partnerships, lot sales, or completed projects.",
    body: "The objective throughout is to transform well-located real estate into lasting residential communities and durable value.",
  },
];

const SitePlan = () => (
  <svg viewBox="0 0 600 380" className="w-full h-auto" role="img" aria-label="Illustrative residential site plan">
    <defs>
      <pattern id="dev-grid" width="24" height="24" patternUnits="userSpaceOnUse">
        <path d="M24 0 L0 0 0 24" fill="none" stroke="#0D2E1B" strokeWidth="0.4" opacity="0.1" />
      </pattern>
    </defs>
    <rect x="0" y="0" width="600" height="380" fill="url(#dev-grid)" />
    {/* parcel boundary */}
    <path d="M40 40 L560 40 L560 340 L40 340 Z" fill="#0D2E1B" opacity="0.03" stroke="#0D2E1B" strokeOpacity="0.35" strokeWidth="1.5" strokeDasharray="6 5" />
    {/* main road (curved) */}
    <path d="M40 130 C 200 130, 200 250, 360 250 S 560 250, 560 250" fill="none" stroke="#C9A875" strokeWidth="14" strokeLinecap="round" opacity="0.55" />
    <path d="M40 130 C 200 130, 200 250, 360 250 S 560 250, 560 250" fill="none" stroke="#0D2E1B" strokeWidth="1" strokeDasharray="6 8" opacity="0.4" />
    {/* cul-de-sac */}
    <circle cx="470" cy="150" r="34" fill="none" stroke="#C9A875" strokeWidth="12" opacity="0.5" />
    <path d="M360 250 C 400 230, 440 190, 470 170" fill="none" stroke="#C9A875" strokeWidth="12" opacity="0.5" strokeLinecap="round" />
    {/* lots (top row) */}
    {Array.from({ length: 6 }).map((_, i) => (
      <rect key={`t${i}`} x={70 + i * 78} y={56} width="64" height="52" rx="2" fill="#ffffff" stroke="#0D2E1B" strokeOpacity="0.4" strokeWidth="1" />
    ))}
    {/* lots (bottom row) */}
    {Array.from({ length: 6 }).map((_, i) => (
      <rect key={`b${i}`} x={70 + i * 78} y={280} width="64" height="48" rx="2" fill="#ffffff" stroke="#0D2E1B" strokeOpacity="0.4" strokeWidth="1" />
    ))}
    {/* green space */}
    <path d="M150 150 q 40 -20 70 10 q 20 30 -10 55 q -40 20 -70 -10 q -20 -30 10 -55 Z" fill="#0D2E1B" opacity="0.08" stroke="#0D2E1B" strokeOpacity="0.25" />
    <text x="168" y="205" fill="#0D2E1B" fontSize="11" fontWeight="600" opacity="0.5" className="font-sans">OPEN SPACE</text>
    {/* labels */}
    <text x="48" y="30" fill="#0D2E1B" fontSize="12" fontWeight="700" opacity="0.55" letterSpacing="1.5" className="font-sans">ILLUSTRATIVE SITE PLAN</text>
  </svg>
);

export default function Development() {
  return (
    <div data-testid="development-page">
      <Seo
        title="Development Approach — Charlotte Land Development"
        description="Redwood Capital's disciplined development process: Identify, Evaluate, Structure, Entitle, Develop, and Create Value on residential land across the Charlotte metro."
        path="/development"
      />
      <PageHero
        overline="Development"
        title="A Structured, Professional Approach to Development"
        subtitle="From identifying strategically located land to executing a development strategy, our disciplined process is designed to create long-term value — and to give owners, brokers, and partners confidence in how we work."
        image={IMAGES.blueprint}
      />

      {/* PROCESS FLOW STRIP */}
      <section className="bg-forest text-white py-10 md:py-12 border-b border-white/10" data-testid="process-flow">
        <Container>
          <div className="flex items-center gap-3 overflow-x-auto pb-2 lg:justify-center">
            {STAGES.map((s, i) => (
              <React.Fragment key={s.n}>
                <div className="flex items-center gap-2.5 shrink-0">
                  <span className="text-gold font-serif text-sm">{s.n}</span>
                  <span className="font-serif text-lg font-semibold whitespace-nowrap">{s.title}</span>
                </div>
                {i < STAGES.length - 1 && <CaretRight size={18} className="text-gold/60 shrink-0" />}
              </React.Fragment>
            ))}
          </div>
        </Container>
      </section>

      {/* TIMELINE */}
      <section className="py-20 md:py-28" data-testid="process-timeline">
        <Container>
          <Reveal>
            <Overline>Our Process</Overline>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-forest tracking-tight max-w-2xl">
              Six disciplined stages, one objective — value creation
            </h2>
          </Reveal>

          <ol className="relative mt-14 space-y-8">
            <span className="hidden sm:block absolute left-[26px] top-4 bottom-4 w-px bg-line" aria-hidden />
            {STAGES.map((s, i) => (
              <Reveal key={s.n} delay={(i % 2) * 0.06}>
                <li className="relative flex gap-5 sm:gap-7">
                  <div className="relative z-10 flex items-center justify-center w-[54px] h-[54px] rounded-full bg-forest text-gold font-serif text-lg font-semibold shrink-0 shadow-[0_6px_18px_rgba(13,46,27,0.15)]">
                    {s.n}
                  </div>
                  <div className="flex-1 bg-white border border-line rounded-sm p-6 sm:p-8 transition-all duration-300 hover:border-gold hover:shadow-[0_14px_36px_rgba(13,46,27,0.08)]">
                    <div className="flex items-center gap-3">
                      <span className="w-10 h-10 flex items-center justify-center rounded-sm bg-forest/5">
                        <Icon name={s.icon} size={22} weight="light" className="text-forest" />
                      </span>
                      <h3 className="font-serif text-2xl font-semibold text-forest">{s.title}</h3>
                    </div>
                    <p className="mt-4 text-forest font-medium leading-relaxed">{s.lead}</p>
                    <p className="mt-2 text-ink leading-relaxed text-[15px]">{s.body}</p>
                    {s.chips && (
                      <div className="mt-5 pt-5 border-t border-line">
                        <p className="text-xs font-bold uppercase tracking-wider text-gold mb-3">{s.chipsLabel}</p>
                        <div className="flex flex-wrap gap-2">
                          {s.chips.map((c) => (
                            <span key={c} className="inline-flex items-center gap-1.5 bg-cream border border-line rounded-sm px-3 py-1.5 text-sm text-forest font-medium">
                              <span className="w-1.5 h-1.5 rounded-full bg-gold" /> {c}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </li>
              </Reveal>
            ))}
          </ol>
        </Container>
      </section>

      {/* SITE PLANNING GRAPHIC */}
      <section className="bg-white border-y border-line py-20 md:py-28" data-testid="site-planning">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
            <Reveal>
              <Overline>Planning &amp; Feasibility</Overline>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-semibold text-forest tracking-tight">
                Turning raw land into a thoughtful plan
              </h2>
              <p className="mt-6 text-ink text-lg leading-relaxed">
                Before a shovel touches the ground, we work through concept planning, yield studies, and
                infrastructure requirements with our engineering and planning partners — testing how a site can
                responsibly support a residential community.
              </p>
              <ul className="mt-8 space-y-3">
                {["Concept & yield studies", "Roadway, grading & utility layout", "Open space & stormwater planning", "Phasing & feasibility modeling"].map((t) => (
                  <li key={t} className="flex items-center gap-3 text-forest font-medium">
                    <span className="w-1.5 h-1.5 rounded-full bg-gold shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="bg-cream border border-line rounded-sm p-4 sm:p-6 shadow-[0_4px_24px_rgba(13,46,27,0.04)]">
                <SitePlan />
                <p className="mt-3 text-xs text-ink/60 text-center italic">
                  Illustrative site plan for demonstration purposes only. Not a representation of a specific project.
                </p>
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* DEVELOPMENT IMAGERY */}
      <section className="py-20 md:py-28" data-testid="development-imagery">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <Reveal className="lg:col-span-7">
              <div className="rounded-sm overflow-hidden border border-line h-full">
                <img src={DEV_IMG.residential} alt="Aerial view of a residential community" loading="lazy" className="w-full h-full min-h-[280px] object-cover" />
              </div>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-5">
              <div className="rounded-sm overflow-hidden border border-line mb-8">
                <img src={DEV_IMG.construction} alt="New residential development site under construction" loading="lazy" className="w-full h-56 object-cover" />
              </div>
              <blockquote className="border-l-2 border-gold pl-6">
                <p className="font-serif text-xl text-forest leading-relaxed">
                  “Our objective is to transform well-located real estate into valuable residential communities and
                  investment opportunities.”
                </p>
              </blockquote>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* PARTNER CTA */}
      <section className="bg-forest text-white py-20 md:py-28 relative overflow-hidden" data-testid="development-cta">
        <img src={IMAGES.forest} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-10" />
        <div className="grain" />
        <Container className="relative">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8">
            <div className="max-w-2xl">
              <Overline>Partner With Us</Overline>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-semibold tracking-tight">
                Working with engineers, planners, and builders
              </h2>
              <p className="mt-5 text-white/70 text-lg leading-relaxed">
                We collaborate with experienced local professionals throughout entitlement and development. If you
                have a project, a parcel, or expertise to bring to the table, we'd like to hear from you.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 shrink-0">
              <Link to="/brokers-partners" className={btn.gold} data-testid="dev-partners-btn">
                Partner With Us <ArrowRight size={18} />
              </Link>
              <Link to="/submit-property" className={btn.outlineLight} data-testid="dev-submit-btn">
                Submit a Property
              </Link>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}
